<template>
  <li class="sidebar-search" role="search">
    <label for="sidebar-menu-search-input" class="visually-hidden">
      Cari fitur atau menu
    </label>
    <div
      ref="searchWrap"
      class="search-wrap"
      @keydown.down.prevent="moveSearchSelection(1)"
      @keydown.up.prevent="moveSearchSelection(-1)"
      @keydown.esc="closeSearch"
    >
      <svg class="search-icon" viewBox="0 0 20 20" fill="none" aria-hidden="true">
        <circle cx="9" cy="9" r="6" stroke="currentColor" stroke-width="1.5" />
        <path d="M15 15l-3-3" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
      </svg>
      <input
        id="sidebar-menu-search-input"
        v-model="searchQuery"
        type="text"
        class="search-field"
        placeholder="Cari menu atau fitur..."
        autocomplete="off"
        spellcheck="false"
        role="combobox"
        aria-autocomplete="list"
        :aria-expanded="searchOpen ? 'true' : 'false'"
        aria-controls="sidebar-menu-search-results"
        @input="handleSearch"
        @focus="openSearch"
        @keydown.enter.prevent="openSelectedResult"
      />

      <button
        v-if="searchQuery"
        type="button"
        class="search-clear"
        aria-label="Hapus pencarian"
        @click="clearSearch"
      >
        &times;
      </button>

      <div
        v-if="searchOpen && searchQuery.trim()"
        id="sidebar-menu-search-results"
        class="search-results"
        role="listbox"
      >
        <button
          v-for="(result, index) in filteredSearchResults"
          :key="`${result.path}-${result.name || index}`"
          type="button"
          class="search-result"
          :class="{ 'search-result--active': index === activeSearchIndex }"
          role="option"
          :aria-selected="index === activeSearchIndex ? 'true' : 'false'"
          @mouseenter="activeSearchIndex = index"
          @mousedown.prevent="navigateToSearchResult(result)"
        >
          <span class="search-result__icon" aria-hidden="true">
            <svg viewBox="0 0 20 20" fill="none">
              <path d="M4 5.5h12M4 10h8M4 14.5h10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
            </svg>
          </span>
          <span class="search-result__content">
            <strong>{{ result.label }}</strong>
            <small>{{ result.path }}</small>
          </span>
          <span class="search-result__arrow" aria-hidden="true">&rarr;</span>
        </button>

        <div v-if="filteredSearchResults.length === 0" class="search-empty">
          Menu “{{ searchQuery.trim() }}” tidak ditemukan
        </div>
      </div>
    </div>
  </li>
</template>

<script>
import { getConfiguration } from "@/core/services/jwt.service.js";

export default {
  name: "SidebarSearch",
  data() {
    return {
      config: getConfiguration() || {},
      searchQuery: "",
      searchDebounce: null,
      searchOpen: false,
      activeSearchIndex: 0,
    };
  },
  computed: {
    searchableRoutes() {
      const entries = [];
      const seenPaths = new Set();
      const clinicName = this.config && this.config.clinic_name
        ? this.config.clinic_name
        : "Medhop";

      const joinPath = (parentPath, routePath) => {
        if (!routePath) return parentPath || "/";
        if (routePath.charAt(0) === "/") return routePath;
        const base = (parentPath || "").replace(/\/$/, "");
        return `${base}/${routePath}`.replace(/\/+/g, "/");
      };

      const collectRoutes = (routes, parentPath = "") => {
        (routes || []).forEach((route) => {
          const fullPath = joinPath(parentPath, route.path);
          const hasDynamicSegment = fullPath.indexOf(":") !== -1;

          if (
            route.component &&
            route.meta &&
            route.meta.title &&
            !hasDynamicSegment &&
            !seenPaths.has(fullPath)
          ) {
            const titleParts = String(route.meta.title).split("|");
            const rawLabel = titleParts.length > 1
              ? titleParts.slice(1).join("|").trim()
              : String(route.meta.title).replace(clinicName, "").trim();
            const fallbackLabel = String(route.name || fullPath)
              .replace(/[-_/]+/g, " ")
              .trim();

            entries.push({
              label: rawLabel || fallbackLabel,
              path: fullPath,
              name: route.name || "",
              keywords: `${rawLabel} ${fallbackLabel} ${fullPath}`.toLowerCase(),
            });
            seenPaths.add(fullPath);
          }

          if (route.children && route.children.length) {
            collectRoutes(route.children, fullPath);
          }
        });
      };

      collectRoutes(this.$router.options.routes);
      return entries.sort((a, b) => a.label.localeCompare(b.label, "id"));
    },
    filteredSearchResults() {
      const query = this.searchQuery.trim().toLowerCase();
      if (!query) return [];

      return this.searchableRoutes
        .filter((route) => route.keywords.indexOf(query) !== -1)
        .sort((a, b) => {
          const aStarts = a.label.toLowerCase().startsWith(query) ? 0 : 1;
          const bStarts = b.label.toLowerCase().startsWith(query) ? 0 : 1;
          return aStarts - bStarts || a.label.localeCompare(b.label, "id");
        })
        .slice(0, 8);
    },
  },
  mounted() {
    document.addEventListener("mousedown", this.handleOutsideSearch);
  },
  beforeDestroy() {
    clearTimeout(this.searchDebounce);
    document.removeEventListener("mousedown", this.handleOutsideSearch);
  },
  methods: {
    handleSearch() {
      this.searchOpen = true;
      this.activeSearchIndex = 0;
      clearTimeout(this.searchDebounce);
      this.searchDebounce = setTimeout(() => {
        this.$emit("search", this.searchQuery.trim());
      }, 300);
    },
    openSearch() {
      this.searchOpen = true;
      this.activeSearchIndex = 0;
    },
    closeSearch() {
      this.searchOpen = false;
      this.activeSearchIndex = 0;
    },
    clearSearch() {
      this.searchQuery = "";
      this.closeSearch();
      this.$nextTick(() => {
        const input = this.$el.querySelector("#sidebar-menu-search-input");
        if (input) input.focus();
      });
    },
    moveSearchSelection(direction) {
      if (!this.searchOpen) this.searchOpen = true;
      const total = this.filteredSearchResults.length;
      if (!total) return;
      this.activeSearchIndex =
        (this.activeSearchIndex + direction + total) % total;
    },
    openSelectedResult() {
      const result = this.filteredSearchResults[this.activeSearchIndex];
      if (result) this.navigateToSearchResult(result);
    },
    navigateToSearchResult(result) {
      if (!result || !result.path) return;
      this.searchQuery = "";
      this.closeSearch();
      if (this.$route.path !== result.path) {
        this.$router.push(result.path);
      }
    },
    handleOutsideSearch(event) {
      if (this.$refs.searchWrap && !this.$refs.searchWrap.contains(event.target)) {
        this.closeSearch();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.sidebar-search {
  position: relative;
  z-index: 20;
  display: block;
  margin: 10px 12px 12px;
  list-style: none;
}

.search-wrap {
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
}

.search-icon {
  position: absolute;
  top: 50%;
  left: 12px;
  z-index: 1;
  width: 16px;
  height: 16px;
  color: #60758c;
  pointer-events: none;
  transform: translateY(-50%);
  transition: color 0.2s ease;
}

.search-field {
  display: block;
  box-sizing: border-box;
  width: 100%;
  height: 38px;
  padding: 0 38px 0 38px !important;
  color: #143263;
  font-size: 0.8rem;
  line-height: 38px;
  background: #ffffff;
  border: 1px solid #d8e1eb;
  border-radius: 9px;
  outline: none;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &::placeholder {
    color: #7b8da3;
    opacity: 1;
  }

  &:hover {
    border-color: #aebdcd;
  }

  &:focus {
    border-color: #55b9b2;
    box-shadow: 0 0 0 3px rgba(30, 149, 143, 0.12);
  }
}

.search-wrap:focus-within .search-icon {
  color: #1e958f;
}

.search-clear {
  position: absolute;
  top: 50%;
  right: 8px;
  z-index: 2;
  width: 24px;
  height: 24px;
  padding: 0;
  color: #6b7f96;
  font-size: 19px;
  line-height: 22px;
  background: transparent;
  border: 0;
  border-radius: 50%;
  cursor: pointer;
  transform: translateY(-50%);

  &:hover,
  &:focus {
    color: #143263;
    background: #eef4f7;
    outline: none;
  }
}

.search-results {
  position: absolute;
  top: calc(100% + 7px);
  left: 0;
  z-index: 1200;
  width: 100%;
  max-height: 320px;
  padding: 6px;
  overflow-y: auto;
  background: #ffffff;
  border: 1px solid #dfe7ef;
  border-radius: 10px;
  box-shadow: 0 14px 32px rgba(10, 39, 72, 0.2);
}

.search-result {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-height: 48px;
  padding: 7px 8px;
  color: #243b55;
  text-align: left;
  background: transparent;
  border: 0;
  border-radius: 8px;
  cursor: pointer;

  &:hover,
  &:focus,
  &--active {
    color: #143263;
    background: linear-gradient(90deg, rgba(20, 96, 143, 0.09), rgba(30, 149, 143, 0.1));
    outline: none;
  }
}

.search-result__icon {
  display: inline-flex;
  flex: 0 0 28px;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  color: #1e958f;
  background: #e9f7f5;
  border-radius: 8px;

  svg {
    width: 15px;
    height: 15px;
  }
}

.search-result__content {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;

  strong,
  small {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  strong {
    color: inherit;
    font-size: 0.76rem;
    font-weight: 650;
  }

  small {
    margin-top: 1px;
    color: #8493a5;
    font-size: 0.62rem;
  }
}

.search-result__arrow {
  flex-shrink: 0;
  color: #7e90a4;
}

.search-empty {
  padding: 16px 10px;
  color: #6f8195;
  font-size: 0.76rem;
  text-align: center;
}

.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
</style>

<style lang="scss">
.aside-minimize:not(.aside-minimize-hover) .sidebar-search {
  display: none;
}
</style>
